import React, { useState } from 'react';
import { Transaction, Property, TransactionCategory, Tenant } from '../types';
import { Calendar, DollarSign, FileText, Tag, Briefcase, Plus, X, Loader2, User } from 'lucide-react';
import { api } from '../services/api';

interface TransactionFormProps {
  onCancel: () => void;
  onSubmit: (data: Transaction) => void;
  properties: Property[];
  tenants: Tenant[];
  transactionCategories?: TransactionCategory[];
  setTransactionCategories?: React.Dispatch<React.SetStateAction<TransactionCategory[]>>;
}

const InputGroup = ({ label, icon: Icon, children }: { label: string, icon?: any, children: React.ReactNode }) => (
  <div className="mb-5">
    <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
    <div className="relative group">
      {Icon && (
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none transition-colors group-focus-within:text-fly-500 z-10">
          <Icon className="h-5 w-5 text-slate-400 group-focus-within:text-fly-500" />
        </div>
      )}
      {children}
    </div>
  </div>
);

const TransactionForm: React.FC<TransactionFormProps> = ({ onCancel, onSubmit, properties, tenants = [], transactionCategories = [], setTransactionCategories }) => {
  const [formData, setFormData] = useState<Partial<Transaction>>({
    date: new Date().toISOString().split('T')[0],
    type: 'Expense'
  });

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [isAddingCat, setIsAddingCat] = useState(false);

  const handleAddCategory = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newCatName.trim() || !setTransactionCategories) return;
      setIsAddingCat(true);
      try {
          const added = await api.transactionCategories.create({ name: newCatName, type: formData.type });
          setTransactionCategories([...transactionCategories, added]);
          setFormData(prev => ({ ...prev, categoryId: added.id })); // Auto-select new category
          setNewCatName('');
          setShowCategoryModal(false);
      } catch (error) {
          alert('فشل في إضافة التصنيف');
      } finally {
          setIsAddingCat(false);
      }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
        const checked = (e.target as HTMLInputElement).checked;
        setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePropertyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const propertyId = e.target.value;
      const propertyName = properties.find(p => String(p.id) === String(propertyId))?.name || '';
      setFormData(prev => ({ ...prev, propertyId, propertyName }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...formData, id: Math.random().toString(), amount: Number(formData.amount) } as Transaction);
  };
  
  const inputClass = "block w-full pr-11 bg-white border border-slate-300 rounded-xl py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-input";
  const selectClass = "block w-full pr-11 bg-white border border-slate-300 rounded-xl py-3 text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-input appearance-none";

  return (
    <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl shadow-xl border border-slate-200 overflow-hidden max-w-2xl mx-auto">
      <div className="bg-white p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 z-10">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">سند قبض / صرف للإيرادات والمصروفات</h2>
          <p className="text-slate-500 text-sm mt-1">تسجيل الإيرادات الإضافية والمصروفات بشكل عام أو خاص</p>
        </div>
      </div>

      <div className="p-8 space-y-6">
        <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm space-y-6">
            <InputGroup label="نوع المعاملة" icon={Briefcase}>
                <div className="flex gap-4">
                    <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.type === 'Expense' ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input type="radio" name="type" value="Expense" checked={formData.type === 'Expense'} onChange={handleInputChange} className="hidden" />
                        <span className="font-bold">سند صرف (مصروف)</span>
                    </label>
                    <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.type === 'Income' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input type="radio" name="type" value="Income" checked={formData.type === 'Income'} onChange={handleInputChange} className="hidden" />
                        <span className="font-bold">سند قبض (إيراد إضافي)</span>
                    </label>
                </div>
            </InputGroup>

            <InputGroup label="التصنيف" icon={Tag}>
                <div className="flex gap-2">
                    <select name="categoryId" onChange={handleInputChange} value={formData.categoryId || ''} className={selectClass} required>
                        <option value="">إختر التصنيف</option>
                        {transactionCategories
                            .filter(cat => cat.type === formData.type)
                            .map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        {transactionCategories.length === 0 && (
                            <option value="" disabled>جاري تحميل التصنيفات...</option>
                        )}
                    </select>
                    {setTransactionCategories && (
                         <button 
                             type="button"
                             onClick={() => setShowCategoryModal(true)}
                             className="bg-slate-100 border border-slate-300 text-slate-600 rounded-xl px-4 hover:bg-slate-200 hover:text-fly-600 transition-all focus:ring-4 focus:ring-fly-500/10 flex items-center justify-center shrink-0"
                             title="إضافة تصنيف جديد"
                         >
                             <Plus size={20} />
                         </button>
                    )}
                </div>
            </InputGroup>

            <InputGroup label="المبلغ (د.ك)" icon={DollarSign}>
                <input type="number" step="0.001" min="0" name="amount" required onChange={handleInputChange} className={inputClass} placeholder="0.000" />
            </InputGroup>

            <InputGroup label="التاريخ" icon={Calendar}>
                <input type="date" name="date" required value={formData.date || ''} onChange={handleInputChange} className={inputClass} />
            </InputGroup>

            <InputGroup label="العقار الخاص بالمعاملة (اختياري)" icon={Briefcase}>
                <select name="propertyId" value={formData.propertyId || ''} onChange={handlePropertyChange} className={selectClass}>
                    <option value="">عام (على مستوى الشركة)</option>
                    {properties.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </InputGroup>

             <InputGroup label="المستأجر الخاص بالمعاملة (اختياري)" icon={User}>
                 <select name="tenantId" value={formData.tenantId || ''} onChange={handleInputChange} className={selectClass}>
                     <option value="">عام / غير محدد</option>
                     {tenants.map(t => (
                         <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>
                     ))}
                 </select>
             </InputGroup>

             <InputGroup label="الوصف / ملاحظات" icon={FileText}>
                <input name="description" onChange={handleInputChange} className={inputClass} placeholder="وصف إضافي للمعاملة..." />
            </InputGroup>
        </div>
      </div>

      <div className="p-6 bg-white border-t border-slate-200 flex justify-center gap-6 sticky bottom-0 z-10">
        <button type="button" onClick={onCancel} className="px-12 py-3.5 bg-white border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all">إلغاء</button>
        <button type="submit" className="px-16 py-3.5 bg-fly-600 text-white font-bold rounded-xl hover:bg-fly-700 shadow-xl shadow-fly-600/30 transition-all transform hover:-translate-y-0.5">حفظ البيانات</button>
      </div>

      {showCategoryModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-sm overflow-hidden animate-spring">
                <div className="flex justify-between items-center p-4 border-b border-slate-100 bg-slate-50">
                    <h3 className="font-bold text-slate-800">إضافة تصنيف جديد</h3>
                    <button type="button" onClick={() => setShowCategoryModal(false)} className="text-slate-400 hover:text-rose-500 transition-colors bg-white rounded-lg p-1 border border-slate-200"><X size={18} /></button>
                </div>
                <div className="p-6">
                    <div className="mb-4">
                        <label className="block text-sm font-bold text-slate-600 mb-2">اسم التصنيف ({formData.type === 'Income' ? 'إيراد' : 'مصروف'})</label>
                        <input 
                            type="text" 
                            name="new_cat_name"
                            autoFocus
                            value={newCatName}
                            onChange={(e) => setNewCatName(e.target.value)}
                            placeholder="مثال: رواتب عمال محددة..."
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all font-bold text-slate-800"
                        />
                    </div>
                    <button 
                        type="button" 
                        onClick={handleAddCategory}
                        disabled={isAddingCat || !newCatName.trim()}
                        className="w-full bg-fly-600 text-white p-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-fly-700 transition-all disabled:opacity-50"
                    >
                        {isAddingCat ? <Loader2 size={18} className="animate-spin" /> : 'إضافة الآن'}
                    </button>
                </div>
            </div>
        </div>
      )}
    </form>
  );
};

export default TransactionForm;